/* eslint-disable @typescript-eslint/no-explicit-any */
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * `import()` の結果から名前付き export を取り出して `lazy()` の形に直す。
 *
 * 素で `import("./X").then((m) => ({ default: m.X }))` と書くと、デプロイ直後に
 * チャンクが消えたときだけ壊れる。Vite の preload ヘルパーは最後に
 * `baseModule().catch(handlePreloadError)` を返していて、`handlePreloadError` は
 * vite:preloadError が preventDefault されると例外を投げずに終わる。つまり
 * import() が **undefined で解決する**。
 *
 * entry.client.tsx はまさにその preventDefault をして読み直しをかけているので、
 * 読み直しが始まるまでの数十ミリ秒に `m.X` を読んでしまい、
 * "Cannot read properties of undefined (reading 'TextLessonPanel')" だけが
 * Sentry に並ぶ。原因がチャンクの 404 だと分からない形になる。
 *
 * ここでは undefined を受けたら解決しない Promise を返す。読み直しは既に
 * 走っているので、Suspense のフォールバックのまま次の読み込みを待たせる方が
 * 画面にもログにも嘘が出ない。
 */
export function resolveNamedModule<M, C>(
  load: () => Promise<M>,
  pick: (module: M) => C,
): Promise<{ default: C }> {
  return Promise.resolve(load()).then((module) => {
    if (!module) {
      if (typeof window === "undefined") {
        // SSR には preload ヘルパーが無いので、ここへ来る道理が無い。
        // 待たせると Worker がそのまま固まるので、素直に落とす
        throw new Error("Dynamic import resolved to undefined during SSR");
      }
      // チャンクが取れなかった。再読み込み待ちなので解決させない
      return new Promise<{ default: C }>(() => {});
    }
    return { default: pick(module) };
  });
}

/** 名前付き export を `lazy()` に渡すための小さな器。 */
export function lazyNamed<M, C extends ComponentType<any>>(
  load: () => Promise<M>,
  pick: (module: M) => C,
): LazyExoticComponent<C> {
  return lazy(() => resolveNamedModule(load, pick));
}
